package com.eladfinish.bible.contest.screens.pairMatching.ui

import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.CustomAccessibilityAction
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.customActions
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.eladfinish.bible.contest.common.ReorderHapticFeedbackType
import com.eladfinish.bible.contest.common.rememberReorderHapticFeedback
import com.eladfinish.bible.contest.screens.pairMatching.model.SortableData
import com.eladfinish.bible.contest.ui.components.mouseVerticalScrollbar
import com.eladfinish.bible.contest.ui.extensions.navigationBarContentPadding
import com.eladfinish.bible.contest.ui.extensions.verticalEdgesFadeEffect
import io.github.aakira.napier.Napier
import sh.calvin.reorderable.ReorderableItem
import sh.calvin.reorderable.rememberReorderableLazyListState

@Composable
fun SortableList(
    data: List<SortableData<String>>,
    onSortCompleted: () -> Unit,
) {
    Column(
        modifier = Modifier
            .padding(horizontal = 10.dp)
            .fillMaxHeight(),
    ) {
        SortableListInternal(
            modifier = Modifier.weight(1f),
            contentPadding = navigationBarContentPadding(top = 24.dp, bottom = 24.dp),
            data = data,
            onSortCompleted = onSortCompleted,
        )
    }
}

@Composable
private fun SortableListInternal(
    modifier: Modifier,
    contentPadding: PaddingValues,
    data: List<SortableData<String>>,
    onSortCompleted: () -> Unit,
) {
    val haptic = rememberReorderHapticFeedback()

    // Saved as ids: the items themselves cannot go into the saved state.
    var order by rememberSaveable { mutableStateOf(data.map { it.id }) }
    val itemsById = remember(data) { data.associateBy { it.id } }
    val list = order.mapNotNull { itemsById[it] }
    val lazyListState = rememberLazyListState()
    val reorderableLazyColumnState = rememberReorderableLazyListState(
        lazyListState = lazyListState,
        onMove = { from, to ->
            order = order.toMutableList().apply {
                add(index = to.index, element = removeAt(from.index))
            }

            val isSortCompleted = order.withIndex().all {
                it.value == it.index
            }

            haptic.performHapticFeedback(ReorderHapticFeedbackType.MOVE)

            Napier.d("ReorderableList_ isSortCompleted? $isSortCompleted, $list")
            if (isSortCompleted) {
                onSortCompleted()
            }
        }
    )

    LazyColumn(
        modifier = modifier
            .fillMaxSize()
            .verticalEdgesFadeEffect(
                fadeHeight = 40.dp,
                showTopFade = lazyListState.canScrollBackward,
                showBottomFade = lazyListState.canScrollForward,
                color = MaterialTheme.colorScheme.surface,
            )
            .mouseVerticalScrollbar(lazyListState),
        state = lazyListState,
        contentPadding = contentPadding,
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        itemsIndexed(items = list, key = { _, item -> item.id }) { index, item ->
            ReorderableItem(state = reorderableLazyColumnState, key = item.id) {
                val interactionSource = remember { MutableInteractionSource() }
                SortableItem(
                    modifier = Modifier
                        .semantics {
                            customActions = listOf(
                                CustomAccessibilityAction(
                                    label = "Move Up",
                                    action = {
                                        if (index > 0) {
                                            order = order.toMutableList().apply {
                                                add(index - 1, removeAt(index))
                                            }
                                            true
                                        } else {
                                            false
                                        }
                                    }
                                ),
                                CustomAccessibilityAction(
                                    label = "Move Down",
                                    action = {
                                        if (index < list.size - 1) {
                                            order = order.toMutableList().apply {
                                                add(index + 1, removeAt(index))
                                            }
                                            true
                                        } else {
                                            false
                                        }
                                    }
                                ),
                            )
                        },
                    handleModifier = Modifier
                        .draggableHandle(
                            onDragStarted = {
                                haptic.performHapticFeedback(ReorderHapticFeedbackType.START)
                            },
                            onDragStopped = {
                                haptic.performHapticFeedback(ReorderHapticFeedbackType.END)
                            },
                            interactionSource = interactionSource,
                        )
                        .clearAndSetSemantics { },
                    text = item.value,
                    originalPosition = item.id,
                    index = index,
                )
            }
        }
    }
}


@Preview(showBackground = true)
@Composable
private fun SortableListPreview() {
    val itemsList = List(size = 20) {
        SortableData(
            id = it,
            value = "#" + it.plus(1),
        )
    }

    SortableList(
        data = itemsList,
        onSortCompleted = {},
    )
}
